import { isSayWeeeApp } from '@/utils/weeeapp';
import Constants from '@/constants/const';
import { fetchFtuOrganicFrequencyExperiment, isFtuOrganicTreatmentGroup } from 'experiments/ftu-organic-frequency';
import { getCookie, getValueFromCookie, setCookie } from '@/utils/cookie';
import { isWeixinBrowser } from './common/device';
import { getbCookie } from '@/utils/common/user';
import { showGoogleOneTap } from './google-one-tap';
import { isLatinoChannel } from './common/latino-app';

/**
 * 利用session token判断是否弹出first time use 弹窗
 * @param key
 */
export const updatePopupCreateAccountSession = () => {
  setCookie(Constants.POPUP_CREATE_ACCOUNT_SESSION, getCookie(Constants.WEEE_SESSION_TOKEN));
};

/**
 * 利用session token判断是否弹出first time use 弹窗
 * @param key
 */
export const shouldShowPopupCreateAccount = (query: any, ctx?) => {
  const ftu = query?.ftu;
  const ftuflags = query?.ftuflags;
  if (!ftu) return false;
  //随session token的过期策略走
  const oldSession = getCookie(Constants.POPUP_CREATE_ACCOUNT_SESSION, ctx);
  const newSession = getCookie(Constants.WEEE_SESSION_TOKEN, ctx);
  if (oldSession) {
    if (hasFtuFlags(ftuflags, Constants.FTU_FLAGS.ALWAYS_SHOW)) return true;
    return oldSession !== newSession;
  }
  if (ftu?.startsWith('popup')) return true;
  return false;
};

/**
 * FTU：首轮三方注册成功后缓存接口 next_url（Constants.FTU_NEXT_URL），refresh 后轮询路径使用。
 */
export const persistFtuNextUrl = (nextUrl: unknown) => {
  if (nextUrl == null || nextUrl === '') return;
  const s = String(nextUrl).trim();
  if (!s) return;
  try {
    sessionStorage.setItem(Constants.FTU_NEXT_URL, s);
  } catch {
    /* ignore */
  }
};

/**
 * 取出并清空 session 中的待定 next_url；accountCreated 路径 goTo / goToFtu 末尾无需再手动 remove。
 */
export const takeFtuNextUrl = (): string => {
  try {
    const raw = sessionStorage.getItem(Constants.FTU_NEXT_URL);
    sessionStorage.removeItem(Constants.FTU_NEXT_URL);
    return (raw || '').trim();
  } catch {
    return '';
  }
};

export const shouldShowAdsList = (ctx?) => {
  const shouldShowAds = getCookie(Constants.SHOULD_SHOW_ADS_LIST, ctx);
  return !!(shouldShowAds && shouldShowAds === getCookie(Constants.WEEE_SESSION_TOKEN, ctx));
};

export const hasFtuFlags = (ftuflags, targetFlag) => {
  ftuflags = parseInt(ftuflags);
  if (ftuflags === targetFlag) return true;
  return !!(ftuflags && (ftuflags & targetFlag) !== 0);
};

const UTM_SOURCE = 'Klaviyo';

const getLandingUrl = (ctx?) => {
  let url = getValueFromCookie(Constants.LANDING_URL, ctx) || '';
  if (!url) return '';
  return decodeURIComponent(url);
};

const hasUtmSource = (ctx?) => {
  const landingUrl = getLandingUrl(ctx);
  if (!!landingUrl && landingUrl.includes(`utm_source=${UTM_SOURCE}`)) {
    return true;
  }
  return false;
};

const ftuPayChannel = (ctx?) => {
  const landingUrl = getLandingUrl(ctx);
  if (!!landingUrl && landingUrl.includes(`ftu=popup`)) {
    return true;
  }
  return false;
};

export const couldShowFtuPopup = ({
  lang,
  isLogin,
  query,
  ctx,
  exp692
}: {
  lang: string;
  isLogin: boolean;
  query: any;
  ctx?: any;
  exp692: boolean;
}) => {
  // 用户utm_source=Klaviyo
  const utmSource = (!!query?.utm_source && query?.utm_source === UTM_SOURCE) || hasUtmSource(ctx);
  // 显示google一键登录
  const googleOneTap = showGoogleOneTap({ isLogin, query, ctx, exp692 });
  // // 如果第一次访问的是分享列表 也不弹ftu
  // const hideFtuForShareList = getCookie('hideFtuForShareList', ctx);
  // if (!!hideFtuForShareList) {
  //   return false;
  // }
  // app打开、微信浏览器打开、Klaviyo用户、google一键登录不展示ftu
  if (isSayWeeeApp(ctx) || isWeixinBrowser(ctx) || utmSource || googleOneTap) return false;
  return !!(
    (!isLogin &&
      shouldShowPopupCreateAccount(query, ctx) &&
      getCookie(Constants.EMAIL_COLLECT_POPUP_SHOWED, ctx) !== 'true') ||
    shouldShowAdsList(ctx)
  );
};

export const couldShowFtuPopupOrganic = ({
  lang,
  isLogin,
  query,
  ctx,
  exp692
}: {
  lang: string;
  isLogin: boolean;
  query: any;
  ctx?: any;
  exp692: boolean;
}) => {
  const ftuflags = query?.ftuflags ?? 48;
  // 付费渠道用户
  const ftuChannel = !!query.ftu || ftuPayChannel(ctx);
  // 显示google一键登录
  const googleOneTap = showGoogleOneTap({ isLogin, query, ctx, exp692 });
  // // 如果第一次访问的是分享列表 也不弹ftu
  // const hideFtuForShareList = getCookie('hideFtuForShareList', ctx);
  // if (!!hideFtuForShareList) {
  //   return false;
  // }
  // 用户utm_source=Klaviyo
  const utmSource = (!!query?.utm_source && query?.utm_source === UTM_SOURCE) || hasUtmSource(ctx);
  if (!!getFtuCookie(ctx)) return false;
  // 已登录、app打开、微信浏览器打开、付费渠道、Klaviyo用户、google一键登录不展示ftu
  if (isLogin || isSayWeeeApp(ctx) || isWeixinBrowser(ctx) || ftuChannel || utmSource || googleOneTap) return false;

  if (hasFtuFlags(ftuflags, Constants.FTU_FLAGS.ALWAYS_SHOW)) return true;

  //随session token的过期策略走
  const oldSession = getValueFromCookie(Constants.POPUP_CREATE_ACCOUNT_SESSION, ctx);
  const newSession = getValueFromCookie(Constants.WEEE_SESSION_TOKEN, ctx);
  if (oldSession === newSession || getCookie(Constants.EMAIL_COLLECT_POPUP_SHOWED, ctx) === 'true') {
    return false;
  }
  return true;
};

/** 实验 751：本次 session 是否允许再弹自然流量 FTU（仅算频次，不含渠道/登录等前置条件） */
function canShowOrganicFtuThisTime(isTreatment: boolean, count: number, lastShowTime: number, now: number) {
  const elapsed = now - lastShowTime;

  if (isTreatment) {
    // 实验组：最多 2 次，两次间隔至少 10 分钟
    if (count >= 2) return false;
    return elapsed >= 10 * 60;
  }

  // 对照组（variation=2）：前 2 次间隔 10 秒，第 3 次起间隔 3 分钟（可继续弹）
  if (count >= 2) return elapsed >= 3 * 60;
  return elapsed >= 10;
}

// 读取 ftu 的展示次数和时间
function readOrganicFtuShowState(ctx?) {
  const session = getCookie(Constants.WEEE_SESSION_TOKEN, ctx);
  const boundSession = getCookie(Constants.FTU_SHOW_SESSION, ctx);
  let count = +(getCookie(Constants.FTU_SHOW_COUNT, ctx) || '0');
  let lastShowTime = +(getCookie(Constants.FTU_SHOW_TIME, ctx) || '0');

  if (boundSession !== session) {
    count = 0;
    lastShowTime = 0;
  }

  return { count, lastShowTime };
}

// 记录 ftu 的展示次数和时间
export const recordOrganicFtuShow = (ctx?) => {
  const { count } = readOrganicFtuShowState(ctx);
  const time = new Date().getTime() / 1000;
  setCookie(Constants.FTU_SHOW_SESSION, getCookie(Constants.WEEE_SESSION_TOKEN, ctx), ctx);
  setCookie(Constants.FTU_SHOW_TIME, `${time}`, ctx);
  setCookie(Constants.FTU_SHOW_COUNT, `${count + 1}`, ctx);
};

export const getFtuCookie = (ctx?) => {
  return getValueFromCookie(Constants.FTU_COOKIE, ctx) || '';
};

export const setUserFtuCookie = (ctx?) => {
  if (!!getFtuCookie(ctx)) return;
  setCookie(Constants.FTU_COOKIE, getbCookie(ctx), ctx, { maxAge: Constants.FTU_COOKIE_EXPIRED_TIME });
};

export const doGoogleOneTapExperiment = async (query: Record<string, any>, ctx?: any) => {
  const userCanceledGoogleOneTap = getCookie(Constants.GSOPTOUT, ctx) === '1';
  if (userCanceledGoogleOneTap) return false;
  const googleOneTap = query?.googleOneTap === 'true';
  if (googleOneTap) return true;
  const latinoChannel = isLatinoChannel();
  if (!latinoChannel) return false;
  return false;
};

export const getOrganicTriggered = async ({
  lang,
  isLogin,
  query,
  ctx,
  exp692
}: {
  lang: string;
  isLogin: boolean;
  query: any;
  ctx?: any;
  exp692: boolean;
}) => {
  if (!couldShowFtuPopupOrganic({ lang, isLogin, query, ctx, exp692 })) {
    return false;
  }

  const abRes = await fetchFtuOrganicFrequencyExperiment(ctx);
  const { count, lastShowTime } = readOrganicFtuShowState(ctx);
  const now = new Date().getTime() / 1000;

  return canShowOrganicFtuThisTime(isFtuOrganicTreatmentGroup(abRes), count, lastShowTime, now);
};
